/** @format */

import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import emailjs from "@emailjs/browser";
import backgroundImage from "../assets/Background.jpg";

const HeroSection = styled.div`
  height: 40vh;
  background-image: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
    url(${backgroundImage});
  background-size: cover;
  background-position: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: -8rem;
  color: white;
`;

const HeroTitle = styled.h1`
  font-family: "century gothic";
  font-size: 4rem;
  text-align: center;
  margin: 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
`;

const ProjectsContainer = styled.div`
  padding: 120px 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 120px 4rem 4rem;
  }
`;

const MessageContainer = styled.div`
  font-family: "calibri";
  font-size: 2rem;
  color: #666;
  line-height: 1.8;
  text-align: center;
  padding: 3rem;
  max-width: 800px;
  margin: 0 auto;
  letter-spacing: 0.5px;
`;

const FormContainer = styled.form`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  background: #f8f8f8;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-family: "calibri";
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.8rem;
  font-family: "calibri";
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  &:focus {
    outline: none;
    border-color: #0066cc;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  font-family: "calibri";
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 150px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #0066cc;
  }
`;

const SubmitButton = styled.button`
  background: #0066cc;
  color: white;
  font-family: "calibri";
  font-size: 1.1rem;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.3s ease;
  &:hover {
    background: #0052a3;
  }
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 4px;
  text-align: center;
  font-family: "calibri";
  ${(props) =>
    props.success
      ? `
    background-color: #e6ffe6;
    color: #006600;
  `
      : `
    background-color: #ffe6e6;
    color: #cc0000;
  `}
`;

const InvestmentOpportunities = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\d*$/.test(value)) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const form = React.useRef();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await emailjs.send(
        "service_pdg_dev",
        "template_pdg_contact",
        {
          to_name: "PDG Team",
          from_name: formData.name,
          from_email: formData.email,
          phone_number: formData.phone,
          message: formData.message,
          reply_to: formData.email,
        },
        "Xt4Hs0Hs_Hs0Hs0Hs"
      );
      setStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending email:", error);
      setStatus("error");
    }

    setLoading(false);
  };

  return (
    <>
      <HeroSection>
        <HeroTitle>Investment Opportunities</HeroTitle>
      </HeroSection>
      <ProjectsContainer>
        <MessageContainer>
          PDG offers investment opportunities in selected signature
          developments. Please contact us for current opportunities
        </MessageContainer>
        <FormContainer ref={form} onSubmit={handleSubmit}>
          <input type='hidden' name='to_name' value='PDG Team' />
          <FormGroup>
            <Label htmlFor='name'>Name</Label>
            <Input
              type='text'
              id='name'
              name='from_name'
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor='email'>Email</Label>
            <Input
              type='email'
              id='email'
              name='from_email'
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor='phone'>Phone</Label>
            <Input
              type='tel'
              id='phone'
              name='phone_number'
              value={formData.phone}
              onChange={handleChange}
              pattern='[0-9]*'
              inputMode='numeric'
              required
            />
          </FormGroup>
          <FormGroup>
            <Label htmlFor='message'>Message</Label>
            <TextArea
              id='message'
              name='message'
              value={formData.message}
              onChange={handleChange}
              required
            />
          </FormGroup>
          <SubmitButton type='submit' disabled={loading}>
            {loading ? "Sending..." : "Submit"}
          </SubmitButton>
          {status && (
            <Message success={status === "success"}>
              {status === "success"
                ? "Thank you for your interest! We will contact you soon."
                : "Something went wrong. Please try again later."}
            </Message>
          )}
        </FormContainer>
      </ProjectsContainer>
      <div style={{ height: "200px" }}></div>
    </>
  );
};

export default InvestmentOpportunities;
